import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FORMATS, TIMEZONES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "habitreat_admin_token";
const STATUSES = ["new", "contacted", "scheduled", "closed"];
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong.";
};

const Login = ({ onAuth }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem(KEY, data.token);
      onAuth(data.token);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="max-w-md mx-auto bg-[#131B2E] border border-[#C5A059]/20 p-10">
      <p className="eyebrow mb-4">Admin</p>
      <h1 className="font-display text-4xl text-slate-50 mb-8">Consultation requests</h1>
      <input data-testid="admin-email-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mb-3 bg-[#0B1120] border border-white/15 focus:border-[#C5A059] outline-none px-4 py-3.5 text-sm text-slate-100" />
      <input data-testid="admin-password-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full mb-3 bg-[#0B1120] border border-white/15 focus:border-[#C5A059] outline-none px-4 py-3.5 text-sm text-slate-100" />
      {error && <p data-testid="admin-login-error" className="text-sm text-red-300 mb-3">{error}</p>}
      <button data-testid="admin-login-submit" disabled={busy} className="btn-bronze w-full py-3.5 text-sm font-medium inline-flex justify-center items-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
      </button>
    </form>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(KEY));
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const logout = useCallback(() => {
    sessionStorage.removeItem(KEY);
    setToken(null);
    setRows([]);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/consultations`, { headers });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) logout();
      else toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [headers, logout]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/consultations/${id}`, { status }, { headers });
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const shown = rows.filter((r) => filter === "all" || (filter === "urgent" ? r.urgent : r.format === filter));

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#070A12] pt-32 pb-20 px-5 sm:px-8 lg:px-12">
      {!token ? (
        <Login onAuth={setToken} />
      ) : (
        <div className="max-w-[1440px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
            <div>
              <p className="eyebrow mb-3">Admin</p>
              <h1 className="font-display text-4xl sm:text-5xl text-slate-50">Consultation requests</h1>
              <p className="text-sm text-slate-400 mt-2" data-testid="admin-count">{rows.length} total · {rows.filter((r) => r.urgent).length} urgent</p>
            </div>
            <div className="flex gap-3">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger data-testid="admin-filter-select" className="w-48 rounded-none bg-[#0B1120] border-white/15 text-slate-100"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0B1120] border-[#C5A059]/30 text-slate-100">
                  <SelectItem value="all">All requests</SelectItem>
                  <SelectItem value="urgent">Urgent only</SelectItem>
                  {FORMATS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <button onClick={logout} data-testid="admin-logout-button" className="px-4 border border-white/15 text-slate-200 hover:border-[#C5A059] inline-flex items-center gap-2 text-sm"><LogOut className="w-4 h-4" /> Sign out</button>
            </div>
          </div>
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" />
          ) : shown.length === 0 ? (
            <p className="text-slate-400" data-testid="admin-empty">No requests yet.</p>
          ) : (
            <div className="space-y-3">
              {shown.map((r, i) => (
                <div key={r.id} data-testid={`admin-lead-row-${i + 1}`} className={`grid md:grid-cols-12 gap-4 p-6 bg-[#131B2E] border ${r.urgent ? "border-red-400/40" : "border-white/10"}`}>
                  <div className="md:col-span-3">
                    <p className="text-slate-50 font-medium">{r.name} {r.urgent && <span className="ml-2 text-[10px] font-data uppercase tracking-widest text-red-300 border border-red-400/40 px-1.5 py-0.5">Urgent</span>}</p>
                    <p className="text-sm text-slate-300 break-all">{r.email}</p>
                    {r.phone && <p className="text-sm text-slate-400">{r.phone}</p>}
                  </div>
                  <div className="md:col-span-3 text-sm">
                    <p className="text-[#E2C68C]">{FORMATS.find((f) => f.id === r.format)?.detail ?? r.format}</p>
                    <p className="text-slate-200">{r.date} · {r.time_slot}</p>
                    <p className="text-slate-400">{TIMEZONES.find((t) => t.id === r.timezone)?.label ?? r.timezone}</p>
                  </div>
                  <p className="md:col-span-4 text-sm text-slate-300 whitespace-pre-line">{r.message || "—"}</p>
                  <div className="md:col-span-2 flex flex-col gap-2">
                    <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                      <SelectTrigger data-testid={`admin-status-select-${i + 1}`} className="rounded-none bg-[#0B1120] border-white/15 text-slate-100 capitalize"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0B1120] border-[#C5A059]/30 text-slate-100">
                        {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <span className="font-data text-[10px] uppercase tracking-widest text-slate-400">Email: {r.email_status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </main>
  );
}
